import React, {
    Component,
    ErrorInfo,
    ReactNode,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardHeader,
    Container,
    Input,
    Label
} from 'reactstrap';
import {buildErrorReport, isChunkLoadError} from '../../lib/errorReport.ts';
import {
    currentUrlWithoutCacheBust,
    isRepeatChunkFailure,
    reloadBypassingCache
} from '../../lib/chunkReloadGuard.ts';

/**
 * Recovery screen for the stale-chunk case (see isChunkLoadError). This is almost never a
 * real fault — the tab was open across a release, or was opened while the release was
 * still landing on the server — so we show a friendly "update available" message instead
 * of the diagnostic report. Reload is manual (a single button) so we never discard
 * unsaved work on the page without the user's say-so.
 *
 * Reload goes through reloadBypassingCache, which stamps the attempt in sessionStorage and
 * cache-busts the URL. If this screen shows again shortly after that stamp, the reload
 * didn't help (stale cached HTML or a mid-deploy 404), so we say so and point the user at
 * the two things that reliably work: wait a moment, or open a fresh tab.
 */
const ChunkReloadFallback: React.FC = () => {
    // Evaluated once on mount: the stamp is written by the button below, and re-reading
    // it after a click would flip the copy mid-navigation.
    const [isRepeat] = useState(isRepeatChunkFailure);

    const handleOpenNewTab = (): void => {
        window.open(currentUrlWithoutCacheBust(), '_blank', 'noopener');
    };

    if (isRepeat) {
        return (
            <Container className="py-5" style={{maxWidth: 560}}>
                <Card className="shadow-sm">
                    <CardHeader className="bg-primary text-white">
                        <h5 className="mb-0">Still finishing the update</h5>
                    </CardHeader>
                    <CardBody className="text-center">
                        <p className="text-muted mb-2">
                            Mixiply was just updated and this tab still
                            couldn&apos;t load the latest version. This normally
                            clears within a few minutes of a release.
                        </p>
                        <p className="text-muted mb-3">
                            Open Mixiply in a new tab, or wait a moment and try
                            again.
                        </p>
                        <div className="d-flex justify-content-center gap-2">
                            <Button color="primary" onClick={handleOpenNewTab}>
                                Open in a new tab
                            </Button>
                            <Button
                                color="secondary"
                                outline
                                onClick={reloadBypassingCache}
                            >
                                Try again
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="py-5" style={{maxWidth: 560}}>
            <Card className="shadow-sm">
                <CardHeader className="bg-primary text-white">
                    <h5 className="mb-0">A new version is available</h5>
                </CardHeader>
                <CardBody className="text-center">
                    <p className="text-muted mb-3">
                        Mixiply has just been updated and part of this page
                        couldn&apos;t load. Reload to pick up the latest
                        version.
                    </p>
                    <Button color="primary" onClick={reloadBypassingCache}>
                        Reload
                    </Button>
                </CardBody>
            </Card>
        </Container>
    );
};

interface FallbackProps {
    error: Error | null;
    componentStack?: string | null;
}

/**
 * Generic recovery screen — a copy-ready diagnostic report (route, browser, user, error +
 * component stack) to send with a support request. Reads context from globals so it works
 * even when the React tree below the failure is broken. Always its own component (never
 * reached via an early return) so its hooks run unconditionally per React's rules.
 */
const SupportReportFallback: React.FC<FallbackProps> = ({
    error,
    componentStack
}) => {
    const report = buildErrorReport(error, componentStack);
    const reportRef = useRef<HTMLInputElement>(null);
    const [copied, setCopied] = useState(false);
    const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (copyResetTimer.current) {
                clearTimeout(copyResetTimer.current);
            }
        },
        []
    );

    const handleCopy = async (): Promise<void> => {
        let ok = false;
        try {
            await navigator.clipboard.writeText(report);
            ok = true;
        } catch {
            // Fallback for non-secure contexts where the Clipboard API is unavailable.
            const el = reportRef.current;
            if (el) {
                el.removeAttribute('readonly');
                el.select();
                try {
                    ok = document.execCommand('copy');
                } catch {
                    ok = false;
                }
                el.setAttribute('readonly', 'true');
                window.getSelection()?.removeAllRanges();
            }
        }
        if (ok) {
            setCopied(true);
            if (copyResetTimer.current) {
                clearTimeout(copyResetTimer.current);
            }
            copyResetTimer.current = setTimeout(() => setCopied(false), 2500);
        }
    };

    const handleReload = (): void => window.location.reload();
    const handleGoHome = (): void => window.location.assign('/');

    return (
        <Container className="py-4" style={{maxWidth: 760}}>
            <Card className="shadow-sm">
                <CardHeader className="bg-danger text-white">
                    <h5 className="mb-0">Something went wrong</h5>
                </CardHeader>
                <CardBody>
                    <p className="text-muted">
                        Mixiply hit an unexpected error and can&apos;t continue
                        on this screen. Any unsaved changes on this page may
                        have been lost.
                    </p>

                    <Alert color="info" className="mb-3">
                        <strong>Reporting this problem?</strong> Please copy the
                        diagnostic details below and include them - they help us
                        pinpoint and fix the issue quickly.
                    </Alert>

                    <Label for="error-report" className="fw-semibold">
                        Diagnostic details
                    </Label>
                    <Input
                        id="error-report"
                        type="textarea"
                        readOnly
                        innerRef={reportRef}
                        value={report}
                        rows={14}
                        style={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            whiteSpace: 'pre'
                        }}
                        onFocus={e => e.currentTarget.select()}
                    />

                    <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
                        <Button color="primary" onClick={handleCopy}>
                            {copied ? 'Copied ✓' : 'Copy details'}
                        </Button>
                        <Button
                            color="secondary"
                            outline
                            onClick={handleReload}
                        >
                            Reload page
                        </Button>
                        <Button
                            color="secondary"
                            outline
                            onClick={handleGoHome}
                        >
                            Go to home
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </Container>
    );
};

/**
 * Picks the friendly reload prompt for the benign stale-chunk case and the diagnostic
 * report for everything else. Holds no hooks itself so the conditional branch is safe.
 */
const ErrorFallback: React.FC<FallbackProps> = ({error, componentStack}) => {
    if (isChunkLoadError(error)) {
        return <ChunkReloadFallback />;
    }
    return (
        <SupportReportFallback error={error} componentStack={componentStack} />
    );
};

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/**
 * App-level error boundary. Catches render-time errors anywhere below it, route elements
 * included (the app uses <BrowserRouter>, which doesn't intercept them the way a data
 * router's errorElement would), and shows the recovery screen. Must be a class component -
 * React only supports error boundaries via getDerivedStateFromError / componentDidCatch.
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = {hasError: false, error: null, errorInfo: null};

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Keep the component stack for the diagnostic report and log for the console.
        this.setState({errorInfo});
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    render(): ReactNode {
        if (!this.state.hasError) {
            return this.props.children;
        }
        return (
            <ErrorFallback
                error={this.state.error}
                componentStack={this.state.errorInfo?.componentStack}
            />
        );
    }
}

export default ErrorBoundary;
